import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Tooltip, Typography } from '@lyearn/ui';
import GroupAutocomplete from '@/modules/corporate/modules/group/components/GroupAutocomplete';
import { GroupTypeEnum, ReviewerConfigInput, ReviewerTypeEnum } from '@/types/schema';

import { validateReviewerGroupError } from '../utils';

function SelectGroup({
  reviewerConfig,
  showError,
  type,
  onReviewerConfigChange,
  className,
}: {
  reviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  type: ReviewerTypeEnum;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const onChange = useCallback(
    (value: any) =>
      onReviewerConfigChange({ ...reviewerConfig, userGroupIds: value ? [value] : undefined }),
    [onReviewerConfigChange, reviewerConfig],
  );

  const error = Boolean(showError && validateReviewerGroupError(reviewerConfig));

  if (type !== ReviewerTypeEnum.Group) {
    return null;
  }

  return (
    <Box className={cn('space-y-8', className)}>
      <Stack alignItems="center" className="ms-8">
        <Typography color="text-text-primary" variant="body-short-02">
          {t('Exercise:Creator.ReviewerGroup')}
        </Typography>
        <Tooltip placement="right" title={t('Exercise:Creator.ReviewerGroupTooltipInfo')!}>
          <Box>
            <InfoOutline className="h-16 w-16 ms-4" fillColorToken="icon-secondary" />
          </Box>
        </Tooltip>
      </Stack>
      <GroupAutocomplete
        crossSubWorkspace
        error={error}
        placeholder={t('Exercise:Creator.ReviewerGroupPlaceholder')}
        selectedValue={reviewerConfig.userGroupIds?.[0]}
        size="large"
        type={GroupTypeEnum.UserCollection}
        onChange={onChange}
      />
      {error ? (
        <Typography className="ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default SelectGroup;
